import { Injectable } from "@angular/core";
import { BaseHttpService } from "./base-http.service";
import { HttpClient } from "@angular/common/http";
import { JwtService } from "./jwt.service";
import { LoaderService } from "./loader.service";
import { BehaviorSubject, map, tap, finalize, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { PlatformModel } from "../models/platform.model";
import { ProductPlatform } from "../models/product-platform.model";
import { UserPlatform } from "../models/user.model";
import { logDebug, logWarn, logError } from '../core/services/logger.service';

@Injectable({
    providedIn: 'root'
  })
  export class UserPlatformService extends BaseHttpService {


     private userSubject = new BehaviorSubject<UserPlatform[]>([]);
      public users$ = this.userSubject.asObservable();

      private currentUserSubject = new BehaviorSubject<UserPlatform | null>(null);
      public currentUser$ = this.currentUserSubject.asObservable();


    private readonly baseApiSpeedPresta = environment.baseApiSpeedpresta;
    private readonly usersPlatformUrl = environment.userPlatformUrl;

        constructor(
        http: HttpClient,
        private loaderService: LoaderService,
        jwtService: JwtService
      ) {
        super(jwtService,http);
        if (jwtService.decodeToken()) {
            this.loadUsers();
            this.loadCurrentUserByEmail();
        }
      }

        private loadUsers(): void {
          try {
            this.loaderService.startLoading();
            const body = {
              status: 'active',
              enterpriseId: this.getUserByToken()?.organization.toLocaleUpperCase(),
              options: { 'options.appId': 'USER' },
              excluded: {}
            };
      
            this.retrieveAllFromKey(
              `${this.baseApiSpeedPresta}${this.getUserByToken()?.organization.toLocaleUpperCase()}/${this.usersPlatformUrl}`,
              body
            )
              .pipe(
                map(sites =>
                  sites.map(site => ({
                    appId: site['options']['appId'],
                    email: site['options']['email'],
                    firstname: site['options']['firstname'],
                    surname: site['options']['surname'],
                  }))
                ),
                finalize(() => this.loaderService.stopLoading())
              )
              .subscribe(users => {
                this.userSubject.next(users as UserPlatform[]);
                // Try to find current user after loading all users
                this.findCurrentUserInList();
              });
          } catch (error) {
            logError('Error loading users:', error);
            this.loaderService.stopLoading();
          }
        }

        /**
         * Load current user by email from JWT token
         */
        private loadCurrentUserByEmail(): void {
          const userFromToken = this.getUserByToken();
          if (!userFromToken || !userFromToken.sub) {
            logWarn('No email found in JWT token');
            return;
          }

          const currentEmail = userFromToken.sub;

          // Try to find in already loaded users first
          this.findCurrentUserInList();

          // If not found, search in API
          if (!this.currentUserSubject.value) {
            this.searchUserByEmail(currentEmail);
          }
        }

        /**
         * Find current user in the loaded users list
         */
        private findCurrentUserInList(): void {
          const userFromToken = this.getUserByToken();
          if (!userFromToken || !userFromToken.sub) {
            return;
          }

          const currentEmail = userFromToken.sub.toLowerCase();
          const users = this.userSubject.value;

          const foundUser = users.find(user =>
            user.email.toLowerCase() === currentEmail
          );

          if (foundUser) {
            this.currentUserSubject.next(foundUser);
          }
        }

        /**
         * Search user by email in API
         */
        private searchUserByEmail(email: string): void {
          try {
            this.loaderService.startLoading();
            const body = {
              status: 'active',
              enterpriseId: this.getUserByToken()?.organization.toLocaleUpperCase(),
              options: {
                'options.appId': 'USER',
                'options.email': email
              },
              excluded: {}
            };

            this.retrieveAllFromKey(
              `${this.baseApiSpeedPresta}${this.getUserByToken()?.organization.toLocaleUpperCase()}/${this.usersPlatformUrl}`,
              body
            )
              .pipe(
                map(users =>
                  users.map(user => ({
                    appId: user['options']['appId'],
                    email: user['options']['email'],
                    firstname: user['options']['firstname'],
                    surname: user['options']['surname'],
                  }))
                ),
                finalize(() => this.loaderService.stopLoading())
              )
              .subscribe({
                next: (users) => {
                  if (users.length > 0) {
                    this.currentUserSubject.next(users[0] as UserPlatform);
                  } else {
                    logWarn(`User with email ${email} not found`);
                  }
                },
                error: (error) => {
                  logError('Error searching user by email:', error);
                }
              });
          } catch (error) {
            logError('Error searching user by email:', error);
            this.loaderService.stopLoading();
          }
        }

        /**
         * Get all users observable
         */
        public getUsers(): Observable<UserPlatform[]> {
          return this.users$;
        }

        /**
         * Get current user observable
         */
        public getCurrentUser(): Observable<UserPlatform | null> {
          return this.currentUser$;
        }

        /**
         * Get current user value (synchronous)
         */
        public getCurrentUserValue(): UserPlatform | null {
          return this.currentUserSubject.value;
        }

        /**
         * Manually search user by email
         */
        public findUserByEmail(email: string): Observable<UserPlatform | null> {
          return this.users$.pipe(
            map(users => {
              const found = users.find(user =>
                user.email.toLowerCase() === email.toLowerCase()
              );
              return found || null;
            })
          );
        }

        /**
         * Reload current user
         */
        public reloadCurrentUser(): void {
          this.loadCurrentUserByEmail();
        }

  }
